import React, { useState, useEffect, useMemo } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { parse, format } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCheck, faFileExport, faClose } from '@fortawesome/free-solid-svg-icons';

import ParticipanteItem from './ParticipanteItem';
import { NotificationMessage } from './NotificationMessage';
import { InputMask } from '@react-input/mask';

registerLocale('pt-BR', ptBR);

function ParticipanteList() {
    const [participantes, setParticipantes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedParticipantes, setSelectedParticipantes] = useState([]);

    const [notification, setNotification] = useState({
        type: 'info',
        message: '',
        show: false,
    });

    const [filters, setFilters] = useState({
        nome: '',
        ativo: '',
        cpf: '',
        rg: '',
        fone: '',
        razao: '',
    });

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [openDateRange, setOpenDateRange] = useState(false);
    const [resultsPerPage, setResultsPerPage] = useState(20);
    
    const navigate = useNavigate();
    const location = useLocation();
    
    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        setFilters(prev => ({
            ...prev,
            dateFrom: start ? format(start, 'dd/MM/yyyy') : '',
            dateTo: end ? format(end, 'dd/MM/yyyy') : '',
        }));
    };

    const updateUrlParams = (newFilters) => {
        const filteredParams = Object.fromEntries(
            Object.entries(newFilters).filter(([_, value]) => value !== '' && value !== null)
        );

        const params = new URLSearchParams(filteredParams).toString();
        navigate(`?${params}`);
    };

    const fetchParticipantes = async () => {
        try {
            const accessToken = localStorage.getItem('access_token');
            
            const response = await fetch(`${baseUrl}/manager/participantes`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Erro ao buscar os participantes.');
            }

            const data = await response.json();
            console.log(data)
            setParticipantes(data.participantes);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        for (const param of searchParams) {
            setFilters(prev => ({
                ...prev,
                [param[0]]: param[1]
            }));
        }
        fetchParticipantes();
    }, []);

    const handleCloseNotification = () => {
        setNotification((prev) => ({ ...prev, show: false }));
    };

    const handleSelectAll = (isChecked) => {
        if (isChecked) {
            setSelectedParticipantes(participantes.map(participante => participante.id));
        } else {
            setSelectedParticipantes([]);
        }
    };

    const isAllSelected = selectedParticipantes.length === participantes.length && participantes.length > 0;

    const handleSelectparticipante = (participanteId) => {
        setSelectedParticipantes(prevSelected => 
            prevSelected.includes(participanteId)
                ? prevSelected.filter(id => id !== participanteId)
                : [...prevSelected, participanteId]
        );
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const updatedFilters = {
            ...filters,
            [name]: value,
        };
        setFilters(updatedFilters);
        updateUrlParams(updatedFilters);
    };

    const handleDeleteSelected = async () => {
        const idsString = selectedParticipantes.join(',');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/manager/participantes/excluir/${idsString}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                setNotification({
                    type: 'error',
                    message: 'Erro ao enviar o pedido ao sistema!',
                    show: true,
                });

                throw new Error('Erro ao excluir os itens.');
            }

            setNotification({
                type: 'success',
                message: 'Participantes excluídos com sucesso',
                show: true
            });

            setSelectedParticipantes([]);
            fetchParticipantes();
        } catch (error) {
            console.error('Erro ao excluir itens:', error);
        }
    };

    const handleApproveSelected = async () => {
        const idsString = selectedParticipantes.join(',');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/manager/participantes/ativar/${idsString}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                setNotification({
                    type: 'error',
                    message: 'Erro ao enviar o pedido ao sistema!',
                    show: true,
                });

                throw new Error('Erro ao aprovar os itens.');
            }

            setNotification({
                type: 'success',
                message: 'Participantes aprovados com sucesso',
                show: true
            });

            setSelectedParticipantes([]);
            fetchParticipantes();
        } catch (error) {
            console.error('Erro ao aprovar itens:', error);
        }
    };

    const handleDisapproveSelected = async () => {
        const idsString = selectedParticipantes.join(',');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/manager/participantes/inativar/${idsString}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                setNotification({
                    type: 'error',
                    message: 'Erro ao enviar o pedido ao sistema!',
                    show: true,
                });

                throw new Error('Erro ao aprovar os itens.');
            }

            setNotification({
                type: 'success',
                message: 'Participantes reprovados com sucesso',
                show: true
            });

            setSelectedParticipantes([]);
            fetchParticipantes();
        } catch (error) {
            console.error('Erro ao aprovar itens:', error);
        }
    };

    const handleExport = async (status) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/manager/relatorios/participantes/${status}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                setNotification({
                    type: 'error',
                    message: 'Erro ao exportar relatório!',
                    show: true,
                });
                throw new Error('Erro ao exportar relatório.');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `participantes_${status}_${format(new Date(), 'dd-MM-yyyy')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setNotification({
                type: 'success',
                message: `Relatório de vendas ${status} exportado com sucesso!`,
                show: true
            });
        } catch (error) {
            console.error('Erro ao exportar:', error);
        }
    };

    const filteredparticipantes = useMemo(() => {
        return participantes.filter(participante => {
            const matchesNome = participante.nome.toLowerCase().includes(filters.nome.toLowerCase());
            const matchesAtivo = filters.ativo === '' || participante.ativo === Number(filters.ativo);
            const matchesCpf = participante.cpf.toLowerCase().includes(filters.cpf.toLowerCase());
            const matchesRG = participante.rg.toLowerCase().includes(filters.rg.toLowerCase());
            const matchesFone = participante.fone.toLowerCase().includes(filters.fone.toLowerCase());
            const matchesRazao = participante.razao.toLowerCase().includes(filters.razao.toLowerCase());

            return matchesNome && matchesCpf && matchesAtivo && matchesRG && matchesFone && matchesRazao;
        });
    }, [participantes, filters]);

    const columns = useMemo(() => [
        {
            id: 'select',
            Header: ({ getToggleAllRowsSelectedProps }) => (
                <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                />
            ),
            Cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={selectedParticipantes.includes(row.original.id)}
                    onChange={() => handleSelectparticipante(row.original.id)}
                />
            ),
            headerClassName: 'p-4 text-left text-sm font-medium uppercase'
        },
        { Header: 'Nome', accessor: 'nome', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Status', accessor: 'ativo', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'CPF', accessor: 'cpf', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'RG', accessor: 'rg', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Telefone', accessor: 'fone', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Loja', accessor: 'razao', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Ações', accessor: '', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
    ], [isAllSelected, selectedParticipantes]);

    const pageParams = new URLSearchParams(window.location.search);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        state: { pageIndex },
        gotoPage,
        nextPage,
        previousPage,
    } = useTable(
        {
            columns,
            data: filteredparticipantes,
            initialState: { pageIndex: pageParams.has('page') ? (pageParams.get('page') -1) : 0, pageSize: resultsPerPage },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        if (location.state?.message) {
            setNotification({
                type: location.state.message.type,
                message: location.state.message.text,
                show: true,
            });
        }
    }, [location.state]);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        if (pageIndex !== 0 || searchParams.has('page')) {
            searchParams.set('page', pageIndex + 1);
            navigate(`?${searchParams.toString()}`, { replace: true });
        }
    }, [pageIndex, navigate]);

    if (loading) return (
        <div className="mx-auto py-10">
            <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
        </div>
    );

    if (error) {
        return <div className="text-2xl text-center text-red-600 font-bold my-10">{error}</div>;
    }

    return (
        <>
            
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_2fr_2fr] gap-4 mb-4">
                <div>
                    <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                    <input
                    type="text"
                    id="nome"
                    name="nome"
                    placeholder="Nome"
                    className="border border-gray-300 text-sm p-2 rounded-md w-full"
                    value={filters.nome}
                    onChange={handleFilterChange}
                    />
                </div>

                <div>
                    <label htmlFor="ativo" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                    id="ativo"
                    name="ativo"
                    className="border border-gray-300 text-sm p-2 rounded-md w-full"
                    value={filters.ativo}
                    onChange={handleFilterChange}
                    >
                    <option value="">---</option>
                    <option value="0">Inativos</option>
                    <option value="1">Ativos</option>
                    </select>
                </div>

                <div>
                    <label htmlFor="cpf" className="block text-sm font-medium text-gray-700 mb-1">CPF</label>
                    <InputMask
                    type="text"
                    id="cpf"
                    name="cpf"
                    placeholder='CPF'
                    mask="___.___.___-__"
                    replacement={{ _: /\d/ }}
                    value={filters.cpf}
                    onChange={handleFilterChange}
                    className="border border-gray-300 text-sm p-2 rounded-md w-full"
                    />
                </div>

                <div>
                    <label htmlFor="rg" className="block text-sm font-medium text-gray-700 mb-1">RG</label>
                    <input
                    type="text"
                    id="rg"
                    name="rg"
                    placeholder="RG"
                    className="border border-gray-300 text-sm p-2 rounded-md w-full"
                    value={filters.rg}
                    onChange={handleFilterChange}
                    />
                </div>

                <div>
                    <label htmlFor="fone" className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                    <InputMask
                    type="text"
                    id="fone"
                    name="fone"
                    placeholder='Telefone'
                    mask="(__) _____-____"
                    replacement={{ _: /\d/ }}
                    value={filters.fone}
                    onChange={handleFilterChange}
                    className="border border-gray-300 text-sm p-2 rounded-md w-full"
                    />
                </div>

                <div>
                    <label htmlFor="razao" className="block text-sm font-medium text-gray-700 mb-1">Razão Social</label>
                    <input
                    type="text"
                    id="razao"
                    name="razao"
                    placeholder="Razão social"
                    className="border border-gray-300 text-sm p-2 rounded-md w-full"
                    value={filters.razao}
                    onChange={handleFilterChange}
                    />
                </div>
                </div>

            <table {...getTableProps()} className="w-full table-auto border-collapse bg-white shadow-md rounded-lg">
                <thead>
                    {headerGroups.map((headerGroup, index) => {
                        const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();

                        return (
                            <tr key={key || index} {...headerGroupProps} className="bg-gray-100 text-left">
                                {headerGroup.headers.map((column, groupIndex) => {
                                    const { key: colKey, ...columnProps } = column.getHeaderProps(column.getSortByToggleProps({ title: undefined }));

                                    return (
                                        <th
                                            key={colKey || groupIndex}
                                            {...columnProps}
                                            className="px-4 py-2 font-semibold text-gray-700 border-b"
                                        >
                                            {column.render('Header')}
                                            {column.isSorted ? (
                                                column.isSortedDesc ? (
                                                    <svg className="fill-gray-500 inline-block mb-1 ml-1" width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M5 0L0 5H10L5 0Z" fill=""></path>
                                                    </svg>
                                                ) : (
                                                    <svg className="fill-gray-500 inline-block mb-1 ml-1" width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" fill=""></path>
                                                    </svg>
                                                )
                                            ) : ''}
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </thead>
                <tbody  {...getTableBodyProps()}>
                    {page.map((row, index) => {
                        prepareRow(row);
                        return (
                            <ParticipanteItem
                                key={row.id}
                                row={row}
                                onSelect={handleSelectparticipante}
                                isSelected={selectedParticipantes.includes(row.original.id)}
                            />
                        );
                    })}
                </tbody>
            </table>

            <div className="flex items-center mt-6 mb-4">
                <nav className="pagination isolate inline-flex space-x-1" aria-label="Pagination">
                    <button 
                        onClick={() => previousPage()} 
                        disabled={!canPreviousPage} 
                        className={`relative inline-flex items-center justify-center rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canPreviousPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                    >
                        <span className="sr-only">Anterior</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"></path>
                        </svg>
                    </button>

                    {pageIndex !== 0 && (
                        <button 
                            onClick={() => gotoPage(0)} 
                            disabled={!canPreviousPage} 
                            className={`relative inline-flex items-center justify-center font-medium rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canPreviousPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                        >
                            1
                        </button>
                    )}

                    {pageIndex > 1 && pageOptions.length > 4 && (
                        <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300">...</span>
                    )}

                    {pageOptions[pageIndex - 1] ? (
                        <button 
                            onClick={() => gotoPage(pageIndex - 1)} 
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageIndex}
                        </button>
                    ) : null}


                    <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-white bg-secondary ring-1 ring-inset ring-secondary hover:bg-slate-700">
                        {pageIndex + 1}
                    </span>

                    {pageOptions[pageIndex + 1] && (
                        <button 
                            onClick={() => gotoPage(pageIndex + 1)} 
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageIndex + 2}
                        </button>
                    )}

                    {pageIndex < pageOptions.length - 3 && (
                        <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-gray-400 ring-1 ring-inset ring-gray-300">...</span>
                    )}

                    {pageIndex < pageOptions.length - 2 && (
                        <button 
                            onClick={() => gotoPage(pageOptions.length - 1)} 
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageOptions.length}
                        </button>
                    )}

                    <button 
                        onClick={() => nextPage()} 
                        disabled={!canNextPage} 
                        className={`relative inline-flex items-center justify-center rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canNextPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                    >
                        <span className="sr-only">Próximo</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                            <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd"></path>
                        </svg>
                    </button>
                </nav>

                <div className="flex ml-auto pl-10 gap-3">
                    <div className="relative">
                        <button 
                            onClick={() => handleExport('ativos')}
                            className="bg-blue-500 text-white text-sm 2xl:text-base px-4 h-9 rounded transition-all hover:bg-blue-600"
                        >
                            <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                            Exportar
                        </button>
                    </div>

                    <button onClick={handleApproveSelected} className="bg-green-500 text-white text-sm 2xl:text-base px-4 h-9 rounded ml-auto transition-all enabled:hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed" disabled={selectedParticipantes.length <= 0 ? true : false}>
                        <FontAwesomeIcon icon={faCheck} className=" mr-2" />
                        Ativar Selecionados
                    </button>

                    <button onClick={handleDisapproveSelected} className="bg-red-500 text-white text-sm 2xl:text-base px-4 h-9 rounded ml-auto transition-all enabled:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed" disabled={selectedParticipantes.length <= 0 ? true : false}>
                        <FontAwesomeIcon icon={faClose} className=" mr-2" />
                        Inativar Selecionados
                    </button>

                    <button onClick={handleDeleteSelected} className="bg-red-500 text-white text-sm 2xl:text-base px-4 h-9 rounded ml-auto transition-all enabled:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed" disabled={selectedParticipantes.length <= 0 ? true : false}>
                        <FontAwesomeIcon icon={faTrash} className=" mr-2" />
                        Excluir Selecionados
                    </button>
                </div>
            </div>

            {notification && notification.show && (
                <NotificationMessage
                    type={notification.type}
                    message={notification.message}
                    show={notification.show}
                    onClose={handleCloseNotification}
                />
            )}
        </>
    );
}

export default ParticipanteList;
